import type { AxisAction, ButtonAction, InputState, Settings } from '@/contracts';

/** Keyboard bindings. Two entries per action give WASD + arrows out of the box. */
const KEY_AXES: Record<string, [AxisAction, number]> = {
  KeyW: ['move.forward', 1], ArrowUp: ['move.forward', 1],
  KeyS: ['move.forward', -1], ArrowDown: ['move.forward', -1],
  KeyA: ['move.steer', -1], ArrowLeft: ['move.steer', -1],
  KeyD: ['move.steer', 1], ArrowRight: ['move.steer', 1],
  KeyI: ['tool.boom', 1], KeyK: ['tool.boom', -1],
  KeyJ: ['tool.stick', -1], KeyL: ['tool.stick', 1],
  KeyU: ['tool.bucket', 1], KeyO: ['tool.bucket', -1],
  KeyQ: ['tool.slew', -1], KeyE: ['tool.slew', 1],
  KeyZ: ['tool.aux', -1], KeyX: ['tool.aux', 1],
};

const KEY_BUTTONS: Record<string, ButtonAction> = {
  Space: 'brake', ShiftLeft: 'handbrake',
  KeyF: 'action.primary', KeyG: 'action.secondary',
  KeyH: 'horn', KeyN: 'lights', KeyB: 'beacon',
  KeyR: 'resetMachine', KeyC: 'cameraCycle', KeyT: 'enterExit',
  Escape: 'ui.menu', Tab: 'ui.jobs', KeyM: 'ui.map', KeyP: 'ui.photo', Backspace: 'ui.back',
};

/** Standard-gamepad axis/button indices → semantic actions. */
const PAD_AXES: [number, AxisAction, number][] = [
  [1, 'move.forward', -1], [0, 'move.steer', 1],
  [2, 'look.x', 1], [3, 'look.y', 1],
];
const PAD_BUTTONS: Record<number, ButtonAction> = {
  0: 'action.primary', 1: 'action.secondary', 2: 'enterExit', 3: 'resetMachine',
  4: 'tool.aux' as unknown as ButtonAction, 6: 'brake', 7: 'action.primary',
  9: 'ui.menu', 8: 'ui.jobs', 10: 'cameraCycle', 11: 'horn',
};

const DEADZONE = 0.14;

/** Radial-ish deadzone with a quadratic response curve for fine hydraulic control. */
function curve(v: number): number {
  const a = Math.abs(v);
  if (a < DEADZONE) return 0;
  const n = (a - DEADZONE) / (1 - DEADZONE);
  return Math.sign(v) * n * n;
}

export class Input implements InputState {
  readonly pointerDelta = { x: 0, y: 0 };
  pointerLocked = false;
  lastDevice: 'keyboard' | 'gamepad' | 'touch' = 'keyboard';

  private readonly keys = new Set<string>();
  private readonly axes = new Map<AxisAction, number>();
  private readonly held = new Set<ButtonAction>();
  private readonly justDown = new Set<ButtonAction>();
  private readonly justUp = new Set<ButtonAction>();
  /** Set by the touch UI each frame; merged on top of keyboard/pad. */
  private readonly touchAxes = new Map<AxisAction, number>();
  private readonly touchButtons = new Set<ButtonAction>();
  private readonly detach: (() => void)[] = [];
  private focusCheckAt = 0;

  constructor(private readonly canvas: HTMLCanvasElement, private readonly settings: Settings) {
    const kd = (e: KeyboardEvent) => {
      // Let the browser keep its own shortcuts (copy, refresh, devtools).
      // Meta also suppresses the OS keyups of anything held alongside it, so
      // drop everything now rather than leave a key stuck down.
      if (e.metaKey) { this.releaseAll(); return; }
      if (e.ctrlKey || e.altKey) return;
      if (e.repeat) return;
      if (KEY_AXES[e.code] || KEY_BUTTONS[e.code]) e.preventDefault();
      this.lastDevice = 'keyboard';
      this.keys.add(e.code);
      const b = KEY_BUTTONS[e.code];
      if (b && !this.held.has(b)) {
        this.held.add(b);
        this.justDown.add(b);
      }
    };
    const ku = (e: KeyboardEvent) => {
      this.keys.delete(e.code);
      const b = KEY_BUTTONS[e.code];
      if (b) {
        this.held.delete(b);
        this.justUp.add(b);
      }
    };
    // Losing focus mid-input otherwise leaves a machine driving itself away.
    const blur = () => this.releaseAll();
    const vis = () => { if (document.hidden) this.releaseAll(); };
    const padGone = () => this.releaseAll();
    const mm = (e: MouseEvent) => {
      if (!this.pointerLocked) return;
      this.pointerDelta.x += e.movementX;
      this.pointerDelta.y += e.movementY;
    };
    const plc = () => { this.pointerLocked = document.pointerLockElement === this.canvas; };

    addEventListener('keydown', kd, { passive: false });
    // Capture phase: a keyup must reach us even if an overlay stops propagation,
    // or its key stays logically held forever.
    addEventListener('keyup', ku, { capture: true });
    addEventListener('blur', blur);
    addEventListener('gamepaddisconnected', padGone);
    addEventListener('mousemove', mm);
    document.addEventListener('visibilitychange', vis);
    document.addEventListener('pointerlockchange', plc);
    this.detach.push(
      () => removeEventListener('keydown', kd),
      () => removeEventListener('keyup', ku, { capture: true }),
      () => removeEventListener('blur', blur),
      () => removeEventListener('gamepaddisconnected', padGone),
      () => removeEventListener('mousemove', mm),
      () => document.removeEventListener('visibilitychange', vis),
      () => document.removeEventListener('pointerlockchange', plc),
    );
  }

  /** Called by the engine once per frame, before module updates. */
  poll(): void {
    // Watchdog for focus losses that never fire blur (popovers, OS overlays):
    // a held key with no document focus is a stuck key, not input.
    if (++this.focusCheckAt >= 30) {
      this.focusCheckAt = 0;
      if ((this.keys.size > 0 || this.held.size > 0) && !document.hasFocus()) this.releaseAll();
    }
    this.axes.clear();

    for (const code of this.keys) {
      const m = KEY_AXES[code];
      if (m) this.axes.set(m[0], (this.axes.get(m[0]) ?? 0) + m[1]);
    }

    for (const pad of navigator.getGamepads?.() ?? []) {
      if (!pad?.connected) continue;
      let active = false;
      for (const [i, action, sign] of PAD_AXES) {
        const v = curve(pad.axes[i] ?? 0);
        if (v !== 0) { active = true; this.axes.set(action, (this.axes.get(action) ?? 0) + v * sign); }
      }
      // Triggers drive the boom/bucket pair for one-handed play.
      const lt = pad.buttons[6]?.value ?? 0;
      const rt = pad.buttons[7]?.value ?? 0;
      if (lt > 0.05 || rt > 0.05) { active = true; this.axes.set('tool.boom', rt - lt); }
      for (const [idx, action] of Object.entries(PAD_BUTTONS)) {
        const btn = pad.buttons[Number(idx)];
        if (!btn) continue;
        if (btn.pressed) {
          active = true;
          if (!this.held.has(action)) { this.held.add(action); this.justDown.add(action); }
        } else if (this.held.has(action)) {
          this.held.delete(action); this.justUp.add(action);
        }
      }
      if (active) this.lastDevice = 'gamepad';
      break; // player one only
    }

    for (const [a, v] of this.touchAxes) this.axes.set(a, (this.axes.get(a) ?? 0) + v);
    for (const b of this.touchButtons) if (!this.held.has(b)) { this.held.add(b); this.justDown.add(b); }

    for (const [a, v] of this.axes) this.axes.set(a, Math.max(-1, Math.min(1, v)));
  }

  /** Called by the engine after module updates — edge sets live exactly one frame. */
  endFrame(): void {
    this.justDown.clear();
    this.justUp.clear();
    this.pointerDelta.x = 0;
    this.pointerDelta.y = 0;
  }

  axis(a: AxisAction): number {
    const v = this.axes.get(a) ?? 0;
    return a === 'look.y' && this.settings.invertLookY ? -v : v;
  }
  down(b: ButtonAction): boolean { return this.held.has(b); }
  pressed(b: ButtonAction): boolean { return this.justDown.has(b); }
  released(b: ButtonAction): boolean { return this.justUp.has(b); }

  /** Touch/virtual-stick surface for the mobile HUD. */
  setTouchAxis(a: AxisAction, v: number): void {
    this.lastDevice = 'touch';
    if (v === 0) this.touchAxes.delete(a); else this.touchAxes.set(a, Math.max(-1, Math.min(1, v)));
  }
  setTouchButton(b: ButtonAction, downNow: boolean): void {
    this.lastDevice = 'touch';
    if (downNow) this.touchButtons.add(b);
    else { this.touchButtons.delete(b); if (this.held.delete(b)) this.justUp.add(b); }
  }

  requestPointerLock(): void { void this.canvas.requestPointerLock?.(); }

  private releaseAll(): void {
    this.keys.clear();
    this.touchAxes.clear();
    this.touchButtons.clear();
    for (const b of this.held) this.justUp.add(b);
    this.held.clear();
    this.axes.clear();
  }

  dispose(): void { for (const d of this.detach) d(); this.detach.length = 0; }
}
